import { useApiClient } from './apiClient';
import { ApiRoutes } from '@/constants/apiConstants';
import type { ClientProfileResponse, ClientProfileRequest, BookingParticipantOptionResponse } from '@/types/client';
import { ClientListItem } from '@/types/client';
import type { ClientChildRequest, ClientChildResponse } from '@/types/clientChildren';
import { ClientChildListItem } from '@/types/clientChildren';
import type { 
  CreateClientInvitationRequest, 
  CompleteInvitationRequest, 
  ClientInvitationResponse 
} from '@/types/clientInvitation';

class ClientService {
  async getClients(): Promise<ClientListItem[]> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<ClientProfileResponse[]>(
      ApiRoutes.Client.GetClients
    )) as unknown as ClientProfileResponse[];

    return response.map((item) => new ClientListItem(item));
  }

  async getBookingParticipantOptions(): Promise<BookingParticipantOptionResponse[]> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<BookingParticipantOptionResponse[]>(
      ApiRoutes.Client.GetBookingParticipantOptions
    )) as unknown as BookingParticipantOptionResponse[];

    return response;
  }

  async getMyBookingParticipantOptions(): Promise<BookingParticipantOptionResponse[]> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<BookingParticipantOptionResponse[]>(
      ApiRoutes.Client.GetMyBookingParticipantOptions
    )) as unknown as BookingParticipantOptionResponse[];

    return response;
  }

  async getClientById(clientId: number): Promise<ClientListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<ClientProfileResponse>(
      `${ApiRoutes.Client.GetClientById}?clientId=${clientId}`
    )) as unknown as ClientProfileResponse;

    return new ClientListItem(response);
  }

  async createClient(request: ClientProfileRequest): Promise<ClientListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<ClientProfileResponse>(
      ApiRoutes.Client.CreateClient,
      request
    )) as unknown as ClientProfileResponse;

    return new ClientListItem(response);
  }

  async updateClient(clientId: number, request: ClientProfileRequest): Promise<ClientListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.put<ClientProfileResponse>(
      `${ApiRoutes.Client.UpdateClient}?clientId=${clientId}`,
      request
    )) as unknown as ClientProfileResponse;

    return new ClientListItem(response);
  }

  async deleteClient(clientId: number): Promise<void> {
    const { apiClient } = await useApiClient();
    await apiClient.delete<void>(`${ApiRoutes.Client.DeleteClient}?clientId=${clientId}`);
  }

  async getClientChildren(clientId: number): Promise<ClientChildListItem[]> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<ClientChildResponse[]>(
      `${ApiRoutes.Client.GetClientChildren}?clientId=${clientId}`
    )) as unknown as ClientChildResponse[];

    return response.map((child) => new ClientChildListItem(child));
  }

  async getClientChildById(clientChildId: number): Promise<ClientChildListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<ClientChildResponse>(
      `${ApiRoutes.Client.GetClientChildById}?clientChildId=${clientChildId}`
    )) as unknown as ClientChildResponse;

    return new ClientChildListItem(response);
  }

  async createClientChild(request: ClientChildRequest): Promise<ClientChildListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<ClientChildResponse>(
      ApiRoutes.Client.CreateClientChild,
      request
    )) as unknown as ClientChildResponse;

    return new ClientChildListItem(response);
  }

  async updateClientChild(
    clientChildId: number,
    request: ClientChildRequest
  ): Promise<ClientChildListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.put<ClientChildResponse>(
      `${ApiRoutes.Client.UpdateClientChild}?clientChildId=${clientChildId}`,
      request
    )) as unknown as ClientChildResponse;

    return new ClientChildListItem(response);
  }

  async deleteClientChild(clientChildId: number): Promise<void> {
    const { apiClient } = await useApiClient();
    await apiClient.delete<void>(
      `${ApiRoutes.Client.DeleteClientChild}?clientChildId=${clientChildId}`
    );
  }

  async promoteClientChild(clientChildId: number): Promise<ClientListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<ClientProfileResponse>(
      `${ApiRoutes.Client.PromoteClientChild}?clientChildId=${clientChildId}`
    )) as unknown as ClientProfileResponse;

    return new ClientListItem(response);
  }

  // #region Client Invitations

  /**
   * Create a new client invitation (Admin only)
   */
  async createInvitation(request: CreateClientInvitationRequest): Promise<ClientInvitationResponse> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<ClientInvitationResponse>(
      ApiRoutes.Client.CreateInvitation,
      request
    )) as unknown as ClientInvitationResponse;

    return response;
  }

  /**
   * Validate invitation token (Public endpoint)
   */
  async validateInvitationToken(token: string): Promise<boolean> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<boolean>(
      `${ApiRoutes.Client.ValidateInvitationToken}?token=${encodeURIComponent(token)}`
    )) as unknown as boolean;

    return response;
  }

  /**
   * Get invitation details by token (Public endpoint)
   */
  async getInvitationByToken(token: string): Promise<ClientInvitationResponse> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<ClientInvitationResponse>(
      `${ApiRoutes.Client.GetInvitationByToken}?token=${encodeURIComponent(token)}`
    )) as unknown as ClientInvitationResponse;

    return response;
  }

  /**
   * Complete client registration using invitation token (Public endpoint)
   */
  async completeInvitation(request: CompleteInvitationRequest): Promise<ClientInvitationResponse> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<ClientInvitationResponse>(
      ApiRoutes.Client.CompleteInvitation,
      request
    )) as unknown as ClientInvitationResponse;

    return response;
  }

  /**
   * Resend invitation email (Admin only)
   */
  async resendInvitation(clientId: number): Promise<void> {
    const { apiClient } = await useApiClient();
    await apiClient.post<void>(
      `${ApiRoutes.Client.ResendInvitation}?clientId=${clientId}`,
      {}
    );
  }

  // #endregion
}

export default new ClientService();
